<template>
  <div v-if="loaded">
    <!-- Row top buttons -->
    <div class="row q-mb-lg">
      <div
        class="col-6"
        style="justify-content: flex-start"
      >
        <q-btn
          class="capitalize ctrl-button"
          color="grey-5"
          :label="getString('action.toOverview')"
          @click="$router.push('/event')"
          icon="keyboard_backspace"
        />
      </div>
      <div class="col-6">
        <q-btn
          style="float: right"
          class="capitalize ctrl-button right q-ml-md"
          :color="$v.data.$anyError ? 'grey-5' : 'ctrl-turquoise'"
          :disable="$v.data.$anyError"
          :label="getString('action.save')"
          @click="save()"
          icon-right="save"
        />
        <q-btn
          v-if="data.id"
          class="q-ml-md capitalize ctrl-button right"
          icon-right="delete"
          color="negative capitalize"
          :label="getString('action.delete')"
          align="right"
          @click="deleteEvent()"
        />
      </div>
    </div>

    <!-- Columns -->
    <div class="row gutter-sm q-mb-lg">
      <!-- Left -->
      <div class="col-md-6 col-sm-12 col-xs-12 q-pa-sm">
        <div class="row q-mb-sm">
          <div class="full-width">
            <p class="card-title">
              {{ getString('general') }}
            </p>

            <div class="card-block">
              <div class="row">
                <q-field
                  :error-label="requiredError"
                  class="full-width q-mb-md"
                >
                  <q-input
                    type="text"
                    :float-label="getString('title')"
                    @blur="$v.data.title.$touch"
                    :error="$v.data.title.$error"
                    v-model="data.title"
                  />
                </q-field>
                <q-datetime
                  format24h
                  class="full-width q-mb-md"
                  format="YYYY-MM-DD HH:mm"
                  :disable="false"
                  :float-label="
                    getString(
                      'module.blog.details.publication.date.datepicker.label'
                    )
                  "
                  :min="new Date()"
                  v-model="data.online"
                  type="datetime"
                />

                <div class="row full-width">
                  <div class="col-6 q-pr-md">
                    <q-datetime
                      format24h
                      class="full-width q-mb-md"
                      format="YYYY-MM-DD HH:mm"
                      :disable="false"
                      :float-label="getString('module.events.field.start')"
                      :min="data.online"
                      v-model="data.start"
                      :error="data.start < data.online"
                      type="datetime"
                    />
                  </div>
                  <div class="col-6">
                    <q-datetime
                      format24h
                      class="full-width q-mb-md"
                      format="YYYY-MM-DD HH:mm"
                      :float-label="getString('module.events.field.end')"
                      :min="data.start"
                      v-model="data.end"
                      :error="data.end < data.start"
                      type="datetime"
                    />
                  </div>
                </div>
                <q-field
                  :error-label="requiredError"
                  class="full-width q-mb-md"
                >
                  <q-editor
                    v-model="data.description"
                    min-height="5rem"
                    class="full-width q-mb-md"
                    :float-label="getString('description')"
                    @blur="$v.data.description.$touch"
                    :error="$v.data.description.$error"
                    :toolbar="[
                    ['bold', 'italic', 'strike', 'underline', 'subscript', 'superscript'],
                    ['token', 'hr', 'link', 'custom_btn'],
                    ['undo', 'redo'],
                    ['viewsource'],
                  ]"
                  />
                </q-field>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Right -->
      <div class="col-md-6 col-sm-12 col-xs-12 q-pl-sm">
        <div class="row q-mb-sm">
          <div class="full-width">
            <p class="card-title">
              {{ getString('additional') }}
            </p>

            <div class="card-block">
              <div class="row">
                <chips-autocomplete
                  v-if="categoryLoaded"
                  class="full-width q-mb-md"
                  :disable="false"
                  :errorLabel="requiredError"
                  :selection="data.categories"
                  :name="getString('block.field.custom.categories')"
                  :labelTerm="'name'"
                  endpoint="category"
                  :resultLabel="'categories'"
                  :promptNewMessage="getString('module.categories.add')"
                  @blur="$v.data.categories.$touch"
                  :error="$v.data.categories.$error"
                />
              </div>

              <div class="row">
                <q-select
                  class="full-width q-mb-md"
                  multiple
                  v-model="data.targets"
                  :disable="false"
                  :options="targetOptions"
                  :float-label="getString('editor.block.header.targets')"
                  @blur="$v.data.targets.$touch"
                  :error="$v.data.targets.$error"
                />
              </div>

              <div class="row">
                <q-toggle
                  class="q-mt-md"
                  color="secondary"
                  v-model="data.internal"
                  :disable="false"
                >
                  <span style="margin-left: 1em">
                    {{ getString('pubilcation.internal') }}
                  </span>
                </q-toggle>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import moment from 'moment';

// Mixins
import fetchDetail from '@/mixins/detailViews/fetchDetail';
import suggestUrl from '@/mixins/suggestUrl';
import saveChanges from '@/mixins/detailViews/saveChanges';

import getString from '@/helpers/GetStrings';

import ChipsAutocomplete from '@/components/ChipsAutocomplete.vue';
import EventBus from '@/event-bus';

export default {
  mixins: [suggestUrl, fetchDetail, saveChanges],
  components: {
    ChipsAutocomplete,
  },
  validations: {
    data: {
      title: {
        required,
      },
      description: {
        required,
      },
      categories: {
        required,
      },
      targets: {
        required,
      },
    },
  },
  data() {
    return {
      loaded: false,
      categoryLoaded: false,
      // Name of the module defined in user rights. eg. event for event.edit, event.add etc..
      permission: 'event',
      // Where data is fetched from
      endpoint: 'event',
      targetsLoaded: false,
      targetOptions: [],
      // The object key from the response object
      responseData: {
        multiple: 'events',
        single: 'event',
      },
      // Used for navigation on the front-end (matching the router)
      module: 'event',
      imageModal: false,
      containsErrors: true,
      endDateDisable: true,
      data: {
        id: null,
        title: '',
        description: '',
        categories: [],
        targets: [],
        internal: false,
        online: new Date(),
        start: new Date(),
        end: new Date(),
      },
      titleSet: false,
    };
  },

  methods: {
    getString,

    getTargets() {
      return this.axios
        .get('target')
        .then((res) => {
          this.targetOptions = res.data
            && res.data.targets
            && res.data.targets.map(target => ({
              label: target.label,
              value: target.id,
            }));
          this.targetsLoaded = true;
        })
        .catch((err) => {
          console.warn(err);
        });
    },
    save() {
      this.$v.data.$touch();
      this.saveChanges();
    },

    deleteEvent() {
      // Dialog
      this.$q
        .dialog({
          title: '',
          message: getString('upload.delete.text').replace(
            '%file%',
            this.$store.state.stringsStore.language === 'en'
              ? 'event'
              : 'dit evenement',
          ),
          ok: getString('dialog.confirm.ok'),
          cancel: getString('dialog.confirm.cancel'),
        })
        .then(() => {
          // delete call
          this.axios
            .delete(`${this.endpoint}/${this.data.id}`)
            .then(() => {
              // Refresh data to see the result
              this.$q.notify({
                message: getString('module.events.edit.delete.single'),
                position: 'top-right',
                timeout: 1000,
                color: 'positive',
              });
              this.$router.push('/event');
            })
            .catch((error) => {
              // Error
              console.warn(error);
            });
        })
        .catch(() => {
          console.warn('Not removed');
        });
    },
    requestParams() {
      return {
        title: this.data.title,
        description: this.data.description,
        online: moment(this.data.online).utc().format('YYYY-MM-DD HH:mm:ss'),
        start: moment(this.data.start).utc().format('YYYY-MM-DD HH:mm:ss'),
        end: moment(this.data.end).utc().format('YYYY-MM-DD HH:mm:ss'),
        categories: this.data.categories.map(c => c.id),
        target: this.data.targets,
        internal: this.data.internal ? 1 : 0,
      };
    },
  },

  computed: {
    requiredError() {
      return this.isNl ? 'Dit veld is verplicht' : 'This field is required';
    },
    isNl() {
      return this.$store.state.stringsStore.language === 'nl';
    },
  },

  async mounted() {
    await this.getTargets();

    if (this.$route.params && this.$route.params.id) {
      this.axios.get(`event/${this.$route.params.id}`).then((res) => {
        if (res.data.event && res.data.event.targets) {
          this.data.targets = res.data.event.targets.map(t => t.id);
        }
        this.categoryLoaded = true;
      });
    } else {
      this.categoryLoaded = true;
    }
    this.loaded = true;

    EventBus.$on('chips-autocomplete:categories', (selection) => {
      this.data.categories = selection;
    });
  },
};
</script>

<style lang="stylus" scoped></style>
